/**
 * CandidateDetailPage — Issue #188.
 *
 * Read-only detail view. Shows candidate stage, masked PII, and any associated
 * resume references (OSS storage keys; no download in this Issue).
 */
import { useEffect, useState } from 'react';
import { Link, useRoute } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_CANDIDATE_STAGE_LABELS,
  useHrCandidates,
  type HrCandidateDto,
  type HrApiError,
} from '@/hooks/useHrIssue188';

function mask(value: string | null | undefined, label: string): string {
  return value && value.length >= 1 ? `${label}***${value}` : '—';
}

export default function CandidateDetailPage() {
  const [, params] = useRoute<{ id: string }>('/hr/candidates/:id');
  const candidateId = params?.id ?? '';
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.CANDIDATE_READ);

  const { fetchCandidate } = useHrCandidates();
  const [candidate, setCandidate] = useState<HrCandidateDto | null>(null);
  const [error, setError] = useState<HrApiError | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canRead || !candidateId) return;
    let active = true;
    setLoading(true);
    setError(null);
    void fetchCandidate(candidateId).then((row) => {
      if (!active) return;
      setLoading(false);
      if (row) setCandidate(row);
    });
    return () => {
      active = false;
    };
  }, [canRead, candidateId, fetchCandidate]);

  if (!canRead) return <AccessDenied resource="候选人详情" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">候选人详情</h1>
          <p className="text-sm text-slate-500">PII 仅展示后四位，明文查看需 reveal 接口与审计。</p>
        </div>
        <Link href="/hr/candidates" className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
          ← 返回列表
        </Link>
      </header>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</Card>
      ) : null}

      {loading ? (
        <Card className="p-6 text-slate-500">加载中…</Card>
      ) : !candidate ? (
        <Card className="p-6 text-slate-500">未找到候选人</Card>
      ) : (
        <>
          <Card className="space-y-2 p-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div><span className="text-slate-500">姓名</span>：{candidate.fullName}</div>
              <div><span className="text-slate-500">阶段</span>：{HR_CANDIDATE_STAGE_LABELS[candidate.stage]}</div>
              <div><span className="text-slate-500">应聘岗位</span>：{candidate.appliedPosition ?? '—'}</div>
              <div><span className="text-slate-500">期望薪资</span>：{candidate.expectedSalary ?? '—'}</div>
              <div><span className="text-slate-500">学历</span>：{candidate.education ?? '—'}</div>
              <div><span className="text-slate-500">工作年限</span>：{candidate.yearsOfExperience ?? '—'}</div>
              <div><span className="text-slate-500">手机</span>：{mask(candidate.phoneLast4, '手机')}</div>
              <div><span className="text-slate-500">邮箱</span>：{mask(candidate.emailLast4, '邮箱')}</div>
              <div><span className="text-slate-500">身份证</span>：{mask(candidate.idCardLast4, '身份证')}</div>
              <div><span className="text-slate-500">来源</span>：{candidate.source}</div>
              <div><span className="text-slate-500">匹配度</span>：{candidate.matchScore ?? '—'}</div>
              <div><span className="text-slate-500">风险提示</span>：{candidate.riskFlags ?? '—'}</div>
            </div>
          </Card>

          {candidate.summary ? (
            <Card className="space-y-1 p-4">
              <div className="text-sm font-medium">AI 摘要 (草稿)</div>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{candidate.summary}</p>
              <p className="text-xs text-slate-400">
                AI 摘要为草稿，不能作为录用 / 淘汰 / 调薪决策依据。
              </p>
            </Card>
          ) : null}

          {candidate.notes ? (
            <Card className="space-y-1 p-4">
              <div className="text-sm font-medium">备注</div>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{candidate.notes}</p>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
